import React from 'react';
import { Chip, Tooltip } from '@mui/material';

/**
 * Whether a recharge came after an AIS-140 CLA already existed, or is the
 * first one on record.
 *
 * The backend compares the vehicle's earliest CLA issue date with the recharge
 * time, so the tag here is only a rendering of its RENEWAL / FRESH value -- the
 * same derivation feeds the payments ledger and the certificate-expiry tracker.
 */
const RECHARGE_TYPES: Record<string, { label: string; color: string; description: string }> = {
  RENEWAL: {
    label: 'Renewal',
    color: '#1565c0',
    description: 'An AIS-140 CLA already existed before this recharge',
  },
  FRESH: {
    label: 'Fresh',
    color: '#2e7d32',
    description: 'No AIS-140 CLA existed before this recharge',
  },
};

const keyFor = (value?: string | null): string => (value ?? '').trim().toUpperCase();

export const rechargeTypeLabel = (value?: string | null): string => {
  const key = keyFor(value);
  return RECHARGE_TYPES[key]?.label ?? (value ?? '');
};

export const rechargeTypeDescription = (value?: string | null): string =>
  RECHARGE_TYPES[keyFor(value)]?.description ?? '';

export const rechargeTypeColor = (value?: string | null): string =>
  RECHARGE_TYPES[keyFor(value)]?.color ?? '#616161';

type RechargeTypeTagProps = {
  value?: string | null;
};

const RechargeTypeTag: React.FC<RechargeTypeTagProps> = ({ value }) => {
  if (!value) return <>—</>;

  const color = rechargeTypeColor(value);
  return (
    <Tooltip title={rechargeTypeDescription(value)} placement="top" arrow>
      <Chip
        size="small"
        label={rechargeTypeLabel(value)}
        sx={{
          height: 20,
          fontSize: '0.7rem',
          fontWeight: 600,
          color,
          backgroundColor: `${color}1A`,
          border: `1px solid ${color}`,
        }}
      />
    </Tooltip>
  );
};

export default RechargeTypeTag;
